import { h, Component } from 'preact';
import util from 'preact-util';

// Curated/AI-generated "Relaterte artikler" section rendered below an article.
//
// PERFORMANCE CONTRACT (high-traffic read path):
// - Renders ONLY from the relatedArticles array already embedded in the article
//   object — never fetches anything to display the links.
// - Returns null (renders nothing) when there are no related articles and the
//   viewer cannot edit.
// - The candidate list for the admin picker is fetched lazily, only when an
//   admin opens the picker, and with skipUpdate so store observables and any
//   cached article lists stay untouched.
//
// Props:
// - stores: app stores (articleStore used for the lazy candidate fetch)
// - article: the article object (reads article.relatedArticles)
// - canEdit: show add/remove controls (admin/media worker)
// - onOpenArticle(id): open a related article (in-pane or new drawer)
// - onChanged(newList): notify parent after a successful save so it can update
//   its own article state (this component never mutates the article prop)
class RelatedArticles extends Component {
    constructor(props) {
        super(props);
        this.state = {
            adding: false,
            search: '',
            candidates: null,
            saving: false,
            highlightIndex: 0,
            prevArticleId: props.article?.id,
        };
    }

    // Navigating to another article resets the picker.
    static getDerivedStateFromProps(props, state) {
        if (props.article?.id !== state.prevArticleId) {
            return {
                prevArticleId: props.article?.id,
                adding: false,
                search: '',
                highlightIndex: 0,
            };
        }
        return null;
    }

    getRelated = () => {
        const { article } = this.props;
        return (article?.relatedArticles || []).filter(r => r && r.id && r.id !== article.id);
    }

    getMatches = () => {
        const { article } = this.props;
        const { search, candidates } = this.state;
        const q = (search || '').trim().toLowerCase();
        if (!q) return [];
        const relatedIds = new Set(this.getRelated().map(r => r.id));
        return (candidates || [])
            .filter(a => a.active !== false && a.id !== article.id && !relatedIds.has(a.id))
            .filter(a => (a.title || '').toLowerCase().includes(q))
            .slice(0, 8);
    }

    save = async (newList) => {
        const { article, onChanged } = this.props;
        if (!article?.id) return;
        this.setState({ saving: true });
        await util.fetchApi(`/api/articles/${article.id}`, { method: 'PATCH' }, { 'relatedArticles-overwrite': newList });
        this.setState({ saving: false });
        if (onChanged) {
            onChanged(newList);
        }
    }

    removeArticle = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('[data-id]').dataset;
        const idInt = parseInt(id, 10);
        const { article } = this.props;
        const newList = (article?.relatedArticles || []).filter(r => r && r.id !== idInt);
        await this.save(newList);
    }

    toggleAdding = async () => {
        const next = !this.state.adding;
        this.setState({ adding: next, search: '', highlightIndex: 0 });
        // Lazy, admin-only: fetch the full help-article list once for the picker.
        if (next && !this.state.candidates) {
            const { articleStore } = this.props.stores;
            const candidates = await articleStore.load({
                query: {
                    hostname: articleStore.currentHostname,
                    category: 'help',
                    all: 1,
                    sort: 'title',
                },
                // The store default (100) silently truncates — there are 390+ help articles.
                limit: 1000,
                skipUpdate: true,
            });
            this.setState({ candidates });
        }
    }

    addById = async (idInt) => {
        const { article } = this.props;
        const { candidates } = this.state;
        const candidate = (candidates || []).find(a => a.id === idInt);
        if (!candidate) return;
        const existing = article?.relatedArticles || [];
        if (existing.some(r => r && r.id === idInt)) return;
        const newList = [...existing, { id: idInt, title: candidate.title || '', manual: true }];
        await this.save(newList);
        this.setState({ search: '', highlightIndex: 0 });
    }

    addClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('[data-id]').dataset;
        await this.addById(parseInt(id, 10));
    }

    searchInput = (e) => {
        this.setState({ search: e.target.value, highlightIndex: 0 });
    }

    searchKeyDown = (e) => {
        const matches = this.getMatches();
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.setState(prev => ({
                highlightIndex: Math.min(prev.highlightIndex + 1, Math.max(matches.length - 1, 0)),
            }));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            this.setState(prev => ({ highlightIndex: Math.max(prev.highlightIndex - 1, 0) }));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const match = matches[Math.min(this.state.highlightIndex, matches.length - 1)];
            if (match) {
                this.addById(match.id);
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            this.setState({ adding: false, search: '', highlightIndex: 0 });
        }
    }

    openArticle = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('[data-id]').dataset;
        const { onOpenArticle } = this.props;
        if (onOpenArticle) {
            onOpenArticle(parseInt(id, 10));
        }
    }

    render() {
        const { canEdit } = this.props;
        const { adding, search, saving, highlightIndex } = this.state;
        const related = this.getRelated();
        if (related.length === 0 && !canEdit) return null;

        const q = (search || '').trim().toLowerCase();
        const matches = this.getMatches();

        return (
            <div class='mt-4 pt-3 border-top'>
                <small class='fw-bold text-body-secondary text-uppercase' style='font-size: 0.7em; letter-spacing: 0.05em;'>
                    Relaterte artikler
                    {saving && <i class='fa-solid fa-spinner fa-spin ms-2' />}
                </small>
                <div class='d-flex flex-column align-items-start gap-1 mt-2'>
                    {related.map(r => (
                        <div key={r.id} class='d-flex flex-row align-items-center'>
                            <button
                                type='button'
                                class='btn btn-sm btn-link text-start text-decoration-none p-0'
                                data-id={r.id}
                                onClick={this.openArticle}
                            >
                                <i class='fa-solid fa-arrow-right me-2 text-primary' />
                                {r.title}
                            </button>
                            {canEdit && (
                                <button
                                    type='button'
                                    class='btn btn-sm btn-link text-danger p-0 ms-2'
                                    data-id={r.id}
                                    onClick={this.removeArticle}
                                    title='Fjern relatert artikkel'
                                >
                                    <i class='fa-solid fa-circle-xmark' />
                                </button>
                            )}
                        </div>
                    ))}
                    {related.length === 0 && (
                        <small class='text-body-secondary fst-italic'>Ingen relaterte artikler enda.</small>
                    )}
                </div>
                {canEdit && (
                    <div class='mt-2'>
                        {adding ? (
                            <div style='max-width: 420px;'>
                                <div class='input-group input-group-sm'>
                                    <input
                                        class='form-control form-control-sm'
                                        type='text'
                                        placeholder='Søk etter artikkel...'
                                        value={search}
                                        onInput={this.searchInput}
                                        onKeyDown={this.searchKeyDown}
                                    />
                                    <button class='btn btn-sm btn-outline-secondary' type='button' onClick={this.toggleAdding}>
                                        <i class='fa-solid fa-xmark' />
                                    </button>
                                </div>
                                {q && (
                                    <div class='list-group shadow-sm mt-1'>
                                        {matches.map((a, idx) => (
                                            <button
                                                key={a.id}
                                                type='button'
                                                class={`list-group-item list-group-item-action py-1 ${idx === highlightIndex ? 'active' : ''}`}
                                                style='font-size: 0.85em;'
                                                data-id={a.id}
                                                onClick={this.addClick}
                                            >
                                                {a.title}
                                            </button>
                                        ))}
                                        {matches.length === 0 && (
                                            <div class='list-group-item py-1 text-body-secondary fst-italic' style='font-size: 0.85em;'>
                                                Ingen treff
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <button
                                type='button'
                                class='btn btn-sm btn-outline-secondary rounded-pill'
                                onClick={this.toggleAdding}
                            >
                                <i class='fa-solid fa-plus' /> Legg til artikkel
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    }
}

export default RelatedArticles;
